import { Button } from '@ordo/ui/button'
import { CalendarOff, Inbox } from 'lucide-react'

interface NoServiceEmptyStateProps {
  weekday?: string
  /**
   * Optional call-to-action rendered under the copy (e.g. "Go to next day
   * with orders" — PROD-178). Omitted → no button, unchanged layout.
   */
  action?: { label: string; onClick: () => void }
  /**
   * The selected week's order window is still open (orders incoming) — so an
   * empty view means "no orders placed yet", not that the window has closed.
   */
  ordersIncoming?: boolean
  /**
   * The school calendar or menu service pattern marks this selected day
   * no-service. Ordering is intentionally not expected even if the surrounding
   * week is still open.
   */
  noServiceDay?: boolean
  /**
   * The selected day is a genuine off day (the kitchen doesn't serve it, e.g. a
   * weekend/holiday). Only then is "No service" correct — a served day that
   * simply closed with zero orders reads "No orders", not "No service".
   */
  isOffDay?: boolean
}

export interface NoServiceCopy {
  title: string
  subtitle: string
  /** `off` → genuine off day (CalendarOff); `orders` → no orders (Inbox). */
  variant: 'off' | 'orders'
}

/**
 * Empty-state copy for the chef Orders view. Four cases:
 *   - school calendar NO_SERVICE day → "No orders for {weekday}"
 *   - off day (weekend/holiday) → "No service on {weekday}"
 *   - window open, nothing ordered yet → "No orders yet for {weekday}"
 *   - window closed, nothing was ordered → "No orders for {weekday}"
 * Pure so it's unit-tested without rendering.
 */
export function noServiceEmptyStateCopy({
  weekday,
  ordersIncoming = false,
  noServiceDay = false,
  isOffDay = false,
}: NoServiceEmptyStateProps): NoServiceCopy {
  if (noServiceDay) {
    return {
      title: weekday ? `No orders for ${weekday}` : 'No orders this week',
      subtitle: weekday ? 'School does not need meal service today.' : 'School does not need meal service this week.',
      variant: 'off',
    }
  }
  if (isOffDay) {
    return {
      title: weekday ? `No service on ${weekday}` : 'No service days this week',
      subtitle: 'Pick a weekday to see what’s on the menu.',
      variant: 'off',
    }
  }
  if (ordersIncoming) {
    return {
      title: weekday ? `No orders yet for ${weekday}` : 'No orders yet',
      subtitle: 'Schools can still place orders — check back before the cutoff.',
      variant: 'orders',
    }
  }
  return {
    title: weekday ? `No orders for ${weekday}` : 'No orders this week',
    subtitle: 'Ordering has closed — no orders were placed.',
    variant: 'orders',
  }
}

export function NoServiceEmptyState({
  weekday,
  ordersIncoming,
  noServiceDay,
  isOffDay,
  action,
}: NoServiceEmptyStateProps) {
  const { title, subtitle, variant } = noServiceEmptyStateCopy({ weekday, ordersIncoming, noServiceDay, isOffDay })
  const Icon = variant === 'off' ? CalendarOff : Inbox

  return (
    <div className="m-2 flex flex-col items-center justify-center gap-3 rounded-2xl bg-muted/40 px-6 py-20 text-center lg:m-0">
      <Icon className="h-7 w-7 text-muted-foreground/70" strokeWidth={1.5} />
      <div className="space-y-1">
        <p className="text-lg font-medium text-foreground">{title}</p>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {action ? (
        <Button type="button" variant="outline" size="sm" className="mt-1" onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  )
}
